<template>
    <div v-if="!isLoading">
        <page-title v-if="isNewLocation" title="Nieuwe locatie toevoegen" icon="pe-7s-global"/>
        <page-title v-else :title="`${$t('omt.gui.location-management.label.title')}: ${$t(location.location_type.display_name)} &rarr; ${location.display_name}`" icon="pe-7s-global"/>

        <div class="row mb-2" :data-location-id="location?.id">
            <div class="col-md-12">
                <ul class="nav nav-tabs mb-4">
                    <li v-for="(tab, index) in visibleTabs" class="nav-item" :key="index">
                        <router-link tag="a"
                                     :to="tab.route"
                                     class="nav-link"
                                     :class="{active:isMatchedRoute(tab.matchRouteNames)}">
                            <i>{{ tab.name }}</i>
                        </router-link>
                    </li>
                </ul>

                <b-alert v-if="!currentTab?.visible()" show variant="danger">
                    {{ $t('omt.gui.location-management.errors.no-permission-for-tab') }}
                </b-alert>

                <router-view v-else
                             :location="location"
                             :location-types="locationTypes"
                             :email-types="emailTypes"
                             :phone-types="phoneTypes"
                             :address-types="addressTypes"
                             :service-types="serviceTypes"
                             @locationUpdated="getLocation"/>
            </div>
        </div>
    </div>
    <font-awesome-icon v-else icon="spinner" spin/>
</template>

<script>
import PageTitle from "../../components/PageTitle";
import {hasPermission, isFeatureEnabled} from "../../helpers/permissions";
export default {
    name: "Edit",
    components: {PageTitle},
    data() {
        return {
            isLoadingLocation: true,
            isLoadingDataTypes: true,
            location: {},
            locationTypes: [],
            emailTypes: [],
            phoneTypes: [],
            addressTypes: [],
            serviceTypes: [],
        }
    },

    computed: {
        isLoading() {
            return this.isLoadingLocation || this.isLoadingDataTypes
        },
        tabs() {
            return [
                {
                    route: {name: 'locationEditContactInfo'},
                    matchRouteNames: ['locationEditContactInfo', 'locationCreate'],
                    name: this.$t('omt.gui.location-management.tabs.label.address-info'),
                    visible: () => {
                        return (this.isNewLocation && this.hasPermission('locations.create'))
                            || (!this.isNewLocation && this.hasAnyPermission(['locations.contactinfo.view', 'locations.contactinfo.edit']))
                    },
                },
                {
                    route: {name: 'locationEditServiceTimes', params: {serviceTypeIdentifier: this.availableServiceTypes?.[0]?.identifier}},
                    matchRouteNames: ['locationEditServiceTimes'],
                    name: this.$t('omt.gui.location-management.tabs.label.service-times'),
                    visible: () => {
                        return !this.isNewLocation && this.hasAnyPermission(['locations.servicetimes.view', 'locations.servicetimes.edit'])
                    },
                },
                {
                    route: {name: 'EditLocationServices'},
                    matchRouteNames: ['EditLocationServices'],
                    name: this.$t('omt.gui.location-management.tabs.label.service-settings'),
                    visible: () => {
                        return !this.isNewLocation && this.hasAnyPermission(['locations.services.view', 'locations.services.edit'])
                    },
                },
                {
                    route: {name: 'EditLocationProperties'},
                    matchRouteNames: ['EditLocationProperties'],
                    name: this.$t('omt.gui.location-management.tabs.label.properties'),
                    visible: () => {
                        return !this.isNewLocation && this.hasPermission('locations.properties.view')
                    },
                },
                {
                    route: {name: 'ShipmentBatchCreate'},
                    matchRouteNames: ['ShipmentBatchCreate'],
                    name: `Batch aanmaken`,
                    visible: () => {
                        return !this.isNewLocation && this.hasPermission('orders.batch.create') && this.isFeatureEnabled('batching') && this.locationCanProcessProducts
                    },
                },
                {
                    route: {name: 'ShipmentBatchView'},
                    matchRouteNames: ['ShipmentBatchView'],
                    name: `Batch: ${this.$route.params.batchIdentifier}`,
                    visible: () => {
                        return this.$route.name === 'ShipmentBatchView'
                    },
                },
                {
                    route: {name: 'GoodsReceiptView'},
                    matchRouteNames: ['GoodsReceiptView'],
                    name: `Goederenontvangst: ${this.$route.params.goodsReceiptIdentifier}`,
                    visible: () => {
                        return this.$route.name === 'GoodsReceiptView'
                    },
                },
                {
                    route: { name: 'GoodsReceiptIndex' },
                    matchRouteNames: 'GoodsReceiptIndex',
                    name: this.$t('omt.gui.menu.label.goods-receipts'),
                    visible: () => {
                        return !this.isNewLocation && this.hasPermission('goodsreceipt.view');
                    },
                },
                {
                    route: { name: 'GoodsReceiptCreate' },
                    matchRouteNames: 'GoodsReceiptCreate',
                    name: this.$t('omt.gui.menu.label.goods-receipt-create'),
                    visible: () => {
                        return this.hasPermission('goodsreceipt.create')
                               && this.$route.name === 'GoodsReceiptCreate';
                    },
                },
                {
                    route: {
                        name: 'ProcessStartWithoutBatch',
                        params: {
                            locationTypeIdentifier: this.$route.params.locationTypeIdentifier,
                            locationIdentifier: this.$route.params.locationIdentifier,
                            processType: 'return',
                        },
                    },
                    matchRouteNames: ['ProcessStartWithoutBatch'],
                    name: `Scan retouren`,
                    visible: () => {
                        return this.isFeatureEnabled('return-orders') && this.hasPermission('orders.batch.process.return') && this.locationCanProcessProducts
                    },
                },
                {
                    route: {name: 'PackageManagement'},
                    matchRouteNames: ['PackageManagement'],
                    name: this.$t('omt.gui.location-management.tabs.label.packaging-management'),
                    visible: () => {
                        return !this.isNewLocation && this.hasPermission('packaging.availableboxes.view')
                    },
                },
                {
                    route: { name: 'AppSettings' },
                    matchRouteNames: ['AppSettings'],
                    name: this.$t('omt.gui.location-management.tabs.label.app-settings'),
                    visible: () => {
                        return !this.isNewLocation && this.hasPermission('locations.appsettings')
                    },
                },
                {
                    route: { name: 'SalesOrderFileImport' },
                    matchRouteNames: ['SalesOrderFileImport'],
                    name: this.$t('omt.gui.location-management.tabs.label.sales-order-file-import'),
                    visible: () => {
                        return this.hasPermission('orders.create') && this.locationCanUploadOrders
                    },
                },
            ]
        },
        visibleTabs() {
            return this.tabs.filter((tab) => (tab.visible() === true));
        },
        isNewLocation() {
            return this.$route.name === 'locationCreate'
        },
        locationCanProcessProducts() {
            return this.location.location_properties
                ?.some(p => p.location_property_key.identifier === 'canProcessProducts' && p.int_value)
        },
        locationCanUploadOrders() {
            return this.location.location_properties
                ?.some(p => p.location_property_key.identifier === 'orderUploadEnabled' && p.int_value)
        },
        availableServiceTypes() {
            if (typeof(this.location.location_type) === 'undefined') {
                return [];
            }
            return this.location.location_type.service_types
                .filter(serviceType => this.hasPermission(`locations.servicetype.view.${serviceType.identifier.replace(/_/g, '')}`))
        },
        currentTab() {
            let currentTab = null

            _.each(this.tabs, tab => {
                _.each(this.$route.matched, route => {
                    if (tab.matchRouteNames.indexOf(route.name) !== -1) {
                        currentTab = tab
                    }
                })
            })

            return currentTab
        }
    },

    // For navigating between tabs:
    beforeRouteUpdate (to, from, next) {
        if (this.$root.hasUnsavedChanges) {
            const confirmNavigationQuestion = this.$t('omt.gui.general.notification.unsaved-changes-confirm-leave');
            const answer = window.confirm(confirmNavigationQuestion);

            if (!answer) {
                next(false);
                return;
            }
        }
        this.$root.hasUnsavedChanges = false;
        next();
    },

    // For navigating between main pages:
    beforeRouteLeave (to, from, next) {
        if (this.$root.hasUnsavedChanges) {
            const confirmNavigationQuestion = this.$t('omt.gui.general.notification.unsaved-changes-confirm-leave');
            const answer = window.confirm(confirmNavigationQuestion);

            if (!answer) {
                next(false);
                return;
            }
        }
        this.$root.hasUnsavedChanges = false;
        next();
    },

    mounted() {
        if (!this.isNewLocation) {
            this.getLocation()
        }
        else {
            this.isLoadingLocation = false
        }

        this.getLocationDataTypes()
            .then(() => {
                this.isLoadingDataTypes = false
            })
    },

    methods: {
        getLocation() {
            return this.$http.get(`/api/rest/locations/${this.$route.params.locationTypeIdentifier}/${this.$route.params.locationIdentifier}`)
                .then(response => {
                    this.location = response.data
                    this.isLoadingLocation = false
                })
                .catch(e => {
                    try {
                        this.$alert(e.response.data.errors[0].message[0], 'danger', true)
                    } catch (e) {
                        this.$alert(`Onbekende fout`, 'danger', true)
                    }
                })
        },
        getLocationDataTypes() {
            let locationTypeIdentifier = this.$route.params.locationTypeIdentifier ? '/' + this.$route.params.locationTypeIdentifier : ''

            return this.$store.getters.getLocationDataTypes
                .then(response => {
                    this.locationTypes = this.$store.state.locationDataTypes.locationTypes
                    this.emailTypes = this.$store.state.locationDataTypes.emailTypes
                    this.phoneTypes = this.$store.state.locationDataTypes.phoneTypes
                    this.addressTypes = this.$store.state.locationDataTypes.addressTypes
                    this.serviceTypes = this.$store.state.locationDataTypes.serviceTypes
                })
        },
        isMatchedRoute(tabRouteNames) {
            let match = false
            _.each(this.$route.matched, route => {
                if (tabRouteNames.indexOf(route.name) !== -1)
                    match = true
            })
            return match
        },
    },
}
</script>
